import React, { useState, useMemo } from 'react';
import { Briefcase, MapPin, Calendar, ChevronDown, ChevronUp, MessageCircle, ExternalLink, Clock, Wrench, Search, ChevronLeft, ChevronRight, Image as ImageIcon, Star, Quote, User, Linkedin, Settings, Eye, Users, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import SkillExperienceCard from './SkillExperienceCard';

const TimelineSection = ({ experiences, cvService, onQuestionClick, showImages, onUpdateExperience }) => {
  const [expandedExperiences, setExpandedExperiences] = useState(new Set());
  
  // États pour le système exploratoire
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' ou 'skills'
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  const [imageIndexes, setImageIndexes] = useState({}); // Pour gérer l'index des images par achievement
  const [showRecommendations, setShowRecommendations] = useState({}); // Pour gérer l'affichage des recommandations par expérience
  const [experienceParams, setExperienceParams] = useState({}); // Pour gérer les paramètres angle/audience par expérience
  const [loadingSections, setLoadingSections] = useState(new Set()); // Pour gérer le chargement des sections individuelles

  const toggleExperience = (expId) => {
    const newExpanded = new Set(expandedExperiences);
    if (newExpanded.has(expId)) {
      newExpanded.delete(expId);
    } else {
      newExpanded.add(expId);
    }
    setExpandedExperiences(newExpanded);
  };

  const toggleRecommendations = (expId) => {
    setShowRecommendations(prev => ({
      ...prev,
      [expId]: !prev[expId]
    }));
  };

  // Basculer entre les modes
  const toggleViewMode = () => {
    setViewMode(viewMode === 'timeline' ? 'skills' : 'timeline');
    setSelectedSkills([]); // Reset skills selection when switching modes
    setSkillSearchQuery(''); // Reset search when switching modes
  };

  // Gestion des compétences sélectionnées
  const toggleSkill = (skillName) => {
    setSelectedSkills(prev => {
      if (prev.includes(skillName)) {
        return prev.filter(s => s !== skillName);
      } else {
        return [...prev, skillName];
      }
    });
  };

  // Clic sur compétence pour basculer en mode compétences
  const handleSkillClick = (skillName) => {
    if (viewMode === 'timeline') {
      setViewMode('skills');
      setSelectedSkills([skillName]);
    } else {
      toggleSkill(skillName);
    }
  };

  // Fonctions pour gérer le carrousel d'images
  const getImageIndex = (achievementId) => imageIndexes[achievementId] || 0;
  
  const setImageIndex = (achievementId, index) => {
    setImageIndexes(prev => ({ ...prev, [achievementId]: index }));
  };

  const nextImage = (achievementId, maxIndex) => {
    const currentIndex = getImageIndex(achievementId);
    setImageIndex(achievementId, (currentIndex + 1) % maxIndex);
  };

  const prevImage = (achievementId, maxIndex) => {
    const currentIndex = getImageIndex(achievementId);
    setImageIndex(achievementId, (currentIndex - 1 + maxIndex) % maxIndex);
  };

  // Méthodes pour gérer les paramètres locaux et le refresh des sections
  const handleSectionReload = async (sectionId, newAngle, newAudience) => {
    console.log('🔄 Reloading CV with new params for section:', { sectionId, newAngle, newAudience });
    
    // Ajouter à la liste des sections en chargement
    setLoadingSections(prev => new Set([...prev, sectionId]));
    
    try {
      // Utiliser le service CV pour recharger tout le CV avec les nouveaux paramètres
      console.log('📡 Updating CV parameters and fetching data...');
      await cvService.updateParameters(cvService.locale, newAngle, newAudience, true);
      
      // Récupérer l'expérience mise à jour depuis le service
      const updatedExperience = cvService.getExperienceById(sectionId);
      console.log('📦 Updated experience received:', updatedExperience);
      
      // Mettre à jour les données dans le composant parent
      if (onUpdateExperience && updatedExperience) {
        onUpdateExperience(sectionId, updatedExperience);
      }
      
      // Mettre à jour les paramètres locaux de cette expérience
      setExperienceParams(prev => ({
        ...prev,
        [sectionId]: { angle: newAngle, audience: newAudience }
      }));
      
      console.log('✅ Section reloaded successfully using CV service');
    } catch (error) {
      console.error('❌ Error reloading section:', error);
    } finally {
      // Retirer de la liste des sections en chargement
      setLoadingSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
    }
  };

  // Méthode pour changer l'angle d'une expérience
  const handleAngleChange = async (experienceId, newAngle) => {
    const currentParams = experienceParams[experienceId] || { angle: cvService.angle, audience: cvService.audience };
    await handleSectionReload(experienceId, newAngle, currentParams.audience);
  };

  // Méthode pour changer l'audience d'une expérience
  const handleAudienceChange = async (experienceId, newAudience) => {
    const currentParams = experienceParams[experienceId] || { angle: cvService.angle, audience: cvService.audience };
    await handleSectionReload(experienceId, currentParams.angle, newAudience);
  };

  // Composant de sélection d'angle pour une expérience
  const AngleSelector = ({ experienceId, currentAngle, isLoading }) => {
    const angles = [
      { value: 'authentique', label: cvService.locale === 'fr' ? 'Authentique' : 'Authentic', icon: User },
      { value: 'parcours', label: cvService.locale === 'fr' ? 'Parcours' : 'Journey', icon: Briefcase },
      { value: 'technique', label: cvService.locale === 'fr' ? 'Technique' : 'Technical', icon: Settings }
    ];

    return (
      <div className="flex items-center space-x-1">
        {angles.map(angle => {
          const Icon = angle.icon;
          const isActive = currentAngle === angle.value;
          return (
            <Button
              key={angle.value}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (!isLoading && !isActive) {
                  handleAngleChange(experienceId, angle.value);
                }
              }}
              disabled={isLoading}
              className={`h-7 px-2 text-xs ${
                isActive 
                  ? 'bg-cyan text-space-950 border-cyan' 
                  : 'bg-space-800/70 text-ink-muted border-hair hover:bg-space-800'
              }`}
              title={`${cvService.locale === 'fr' ? 'Angle' : 'Angle'}: ${angle.label}`}
            >
              <Icon size={12} className="mr-1" />
              {angle.label}
            </Button>
          );
        })}
      </div>
    );
  };

  // Composant de sélection d'audience pour une expérience
  const AudienceSelector = ({ experienceId, currentAudience, isLoading }) => {
    const audiences = [
      { value: 'rh', label: 'RH', icon: Users },
      { value: 'cto', label: 'CTO', icon: Settings },
      { value: 'general', label: cvService.locale === 'fr' ? 'Général' : 'General', icon: Eye }
    ];

    return (
      <div className="flex items-center space-x-1">
        {audiences.map(audience => {
          const Icon = audience.icon;
          const isActive = currentAudience === audience.value;
          return (
            <Button
              key={audience.value}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (!isLoading && !isActive) {
                  handleAudienceChange(experienceId, audience.value);
                }
              }}
              disabled={isLoading}
              className={`h-7 px-2 text-xs ${
                isActive 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-space-800/70 text-ink-muted border-hair hover:bg-space-800'
              }`}
              title={`${cvService.locale === 'fr' ? 'Audience' : 'Audience'}: ${audience.label}`}
            >
              <Icon size={12} className="mr-1" />
              {audience.label}
            </Button>
          );
        })}
      </div>
    );
  };

  // Composant carrousel d'images pour le mode Timeline
  const TimelineImageCarousel = ({ images, achievementId }) => {
    if (!images || images.length === 0 || !showImages) return null;

    const currentIndex = getImageIndex(achievementId);

    return (
      <div className="bg-space-800/60 p-4 rounded-lg border border-hair mb-4">
        <h5 className="text-sm font-semibold mb-3 text-ink-muted flex items-center">
          <ImageIcon size={14} className="mr-2" />
          {cvService.locale === 'fr' ? 'Illustrations' : 'Illustrations'}
        </h5>
        <div className="relative bg-space-800 rounded-lg overflow-hidden border border-hair">
          {/* Image principale */}
          <div className="relative h-48 overflow-hidden">
            <img
              src={images[currentIndex]}
              alt={`Illustration ${currentIndex + 1}`}
              className="w-full h-full object-cover transition-all duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback en cas d'erreur de chargement */}
            <div className="absolute inset-0 bg-space-700 flex items-center justify-center text-ink-dim hidden">
              <ImageIcon size={32} />
            </div>
          </div>

          {/* Contrôles de navigation (si plusieurs images) */}
          {images.length > 1 && (
            <>
              {/* Boutons précédent/suivant */}
              <button
                onClick={() => prevImage(achievementId, images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-all duration-200"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => nextImage(achievementId, images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-all duration-200"
              >
                <ChevronRight size={16} />
              </button>

              {/* Indicateurs de pagination */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setImageIndex(achievementId, index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentIndex
                        ? 'bg-space-800 shadow-md'
                        : 'bg-space-900/40 hover:bg-space-700/70'
                    }`}
                  />
                ))}
              </div>

              {/* Compteur d'images */}
              <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Composant pour afficher les recommandations
  const RecommendationsSection = ({ recommendations, experienceId }) => {
    if (!recommendations || recommendations.length === 0) return null;

    const isExpanded = showRecommendations[experienceId];

    return (
      <div className="bg-gradient-to-br from-amber/10 to-amber/10 p-4 rounded-lg border border-amber/30 shadow-sm mb-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleRecommendations(experienceId)}
        >
          <div className="flex items-center">
            <Star size={14} className="mr-2 text-amber" />
            <h5 className="text-sm font-semibold text-amber-soft">
              {cvService.locale === 'fr' ? 'Recommandations' : 'Recommendations'}
            </h5>
            <Badge variant="secondary" className="ml-2 bg-amber/15 text-amber-soft text-xs">
              {recommendations.length}
            </Badge>
          </div>
          <ChevronDown 
            size={16} 
            className={`text-amber transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>

        {isExpanded && (
          <div className="mt-4 space-y-3">
            {recommendations.map((recommendation, index) => (
              <div key={index} className="bg-space-800/70 p-4 rounded-lg border border-amber/20 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber to-amber-soft rounded-full flex items-center justify-center">
                      <User size={14} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h6 className="text-sm font-semibold text-ink">
                          {recommendation.author}
                        </h6>
                        {recommendation.linkedin && (
                          <a
                            href={recommendation.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan hover:text-cyan-soft transition-colors"
                            title={cvService.locale === 'fr' ? 'Voir le profil LinkedIn' : 'View LinkedIn profile'}
                          >
                            <Linkedin size={14} />
                          </a>
                        )}
                      </div>
                      <span className="text-xs text-ink-dim">
                        {recommendation.date}
                      </span>
                    </div>
                    <div className="relative">
                      <Quote size={12} className="absolute -top-1 -left-1 text-amber" />
                      <p className="text-base text-ink-muted leading-relaxed pl-3 italic">
                        "{recommendation.text}"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Extraire toutes les compétences avec leur nombre d'occurrences
  const allSkills = useMemo(() => {
    const skillsCount = new Map();
    experiences?.forEach(exp => {
      exp.data?.forEach(achievement => {
        achievement.skillsUsed?.forEach(skill => {
          const skillName = cvService.getTranslatedValue(skill.id);
          skillsCount.set(skillName, (skillsCount.get(skillName) || 0) + 1);
        });
      });
    });
    
    return Array.from(skillsCount.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [experiences, cvService, cvService.locale]); // Ajout de cvService.locale pour recalculer lors du changement de langue

  // Filtrer les compétences selon la recherche
  const filteredSkills = useMemo(() => {
    if (!skillSearchQuery.trim()) {
      return allSkills;
    }
    
    const query = skillSearchQuery.toLowerCase();
    return allSkills.filter(skill => 
      skill.name.toLowerCase().includes(query)
    );
  }, [allSkills, skillSearchQuery]);

  // Effet pour mettre à jour les compétences sélectionnées et vider la recherche lors du changement de langue
  React.useEffect(() => {
    // Vider la barre de recherche lors du changement de langue
    setSkillSearchQuery('');
    
    if (selectedSkills.length > 0) {
      // Recalculer les compétences sélectionnées dans la nouvelle langue
      const updatedSelectedSkills = [];
      
      experiences?.forEach(exp => {
        exp.data?.forEach(achievement => {
          achievement.skillsUsed?.forEach(skill => {
            const skillName = cvService.getTranslatedValue(skill.id);
            // Si cette compétence était sélectionnée dans l'ancienne langue, l'ajouter dans la nouvelle langue
            if (selectedSkills.some(selected => {
              // Vérifier si c'est la même compétence en comparant les IDs
              const skillId = typeof skill.id === 'object' ? skill.id : { fr: skill.id, en: skill.id };
              const selectedInFr = cvService.getTranslatedValue(skillId, 'fr');
              const selectedInEn = cvService.getTranslatedValue(skillId, 'en');
              return selected === selectedInFr || selected === selectedInEn;
            })) {
              if (!updatedSelectedSkills.includes(skillName)) {
                updatedSelectedSkills.push(skillName);
              }
            }
          });
        });
      });
      
      if (updatedSelectedSkills.length !== selectedSkills.length || 
          !updatedSelectedSkills.every(skill => selectedSkills.includes(skill))) {
        setSelectedSkills(updatedSelectedSkills);
      }
    }
  }, [cvService.locale]); // Se déclenche uniquement lors du changement de langue

  // Filtrer les expériences selon les compétences sélectionnées
  const filteredData = useMemo(() => {
    if (viewMode === 'timeline' || selectedSkills.length === 0) {
      return experiences;
    }

    // Mode compétences : filtrer les achievements qui contiennent AU MOINS UNE des compétences sélectionnées
    const filtered = [];
    experiences?.forEach(exp => {
      const matchingAchievements = exp.data?.filter(achievement => {
        return selectedSkills.some(selectedSkill => 
          achievement.skillsUsed?.some(skill => 
            cvService.getTranslatedValue(skill.id).toLowerCase() === selectedSkill.toLowerCase()
          )
        );
      });

      if (matchingAchievements && matchingAchievements.length > 0) {
        filtered.push({
          ...exp,
          data: matchingAchievements
        });
      }
    });

    // Trier chronologiquement (plus récent en premier)
    return filtered.sort((a, b) => new Date(b.dateRange.from) - new Date(a.dateRange.from));
  }, [experiences, selectedSkills, viewMode, cvService]);

  const renderSkillLevel = (level) => {
    if (!level) return null;
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full ${
              i < level ? 'bg-cyan' : 'bg-space-600'
            }`}
          />
        ))}
      </div>
    );
  };

  if (!experiences || experiences.length === 0) return null;

  return (
    <div className="mb-2">
      {/* Contenu principal */}
      <div className="backdrop-blur-md rounded-xl p-4 border border-hair shadow-xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center mr-4 shadow-lg">
              <Briefcase className="text-white" size={20} />
            </div>
            <h2 className="text-2xl font-bold text-ink tracking-tight">
              {cvService.locale === 'fr' ? 'Parcours Professionnel' : 'Professional Journey'}
            </h2>
          </div>

          {/* Switch entre modes */}
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'outline'}
              size="sm"
              onClick={() => viewMode !== 'timeline' && toggleViewMode()}
              className="flex items-center space-x-2 transition-all duration-200"
            >
              <Clock size={16} />
              <span>{cvService.locale === 'fr' ? 'Parcours' : 'Timeline'}</span>
            </Button>
            <Button
              variant={viewMode === 'skills' ? 'default' : 'outline'}
              size="sm"
              onClick={() => viewMode !== 'skills' && toggleViewMode()}
              className="flex items-center space-x-2 transition-all duration-200"
            >
              <Wrench size={16} />
              <span>{cvService.locale === 'fr' ? 'Compétences' : 'Skills'}</span>
            </Button>
          </div>
        </div>
        
        {/* Container avec sidebar dynamique */}
        <div className="flex gap-6">
          {/* Sidebar dynamique - seulement en mode compétences */}
          {viewMode === 'skills' && (
            <div className="w-64 flex-shrink-0">
              {/* Sidebar Compétences (mode compétences) */}
              <div className="bg-space-800/60 backdrop-blur-sm rounded-lg p-4 border border-hair shadow-sm">
                <h3 className="text-sm font-semibold text-ink-muted mb-3 flex items-center">
                  <Wrench size={14} className="mr-2" />
                  {cvService.locale === 'fr' ? 'Compétences' : 'Skills'}
                  {selectedSkills.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {selectedSkills.length}
                    </Badge>
                  )}
                </h3>
                
                {/* Barre de recherche */}
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-dim" />
                  <input
                    type="text"
                    placeholder={cvService.locale === 'fr' ? 'Rechercher une compétence...' : 'Search skills...'}
                    value={skillSearchQuery}
                    onChange={(e) => setSkillSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs border border-hair rounded-lg bg-space-800/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-cyan focus:border-transparent placeholder-ink-dim"
                  />
                  {skillSearchQuery && (
                    <button
                      onClick={() => setSkillSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-ink-dim hover:text-ink-muted transition-colors"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {filteredSkills.length === 0 ? (
                    <div className="text-center py-4 text-ink-dim">
                      <p className="text-xs">
                        {cvService.locale === 'fr' ? 'Aucune compétence trouvée' : 'No skills found'}
                      </p>
                    </div>
                  ) : (
                    filteredSkills.map(skill => (
                    <Button
                      key={skill.name}
                      variant={selectedSkills.includes(skill.name) ? "default" : "ghost"}
                      size="sm"
                      onClick={() => toggleSkill(skill.name)}
                      className={`w-full justify-between text-left text-xs h-8 ${
                        selectedSkills.includes(skill.name) 
                          ? 'bg-cyan text-space-950 hover:bg-cyan-soft' 
                          : 'text-ink-muted hover:text-ink hover:bg-space-700'
                      }`}
                    >
                      <span>{skill.name}</span>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs h-5 px-2 ${
                          selectedSkills.includes(skill.name)
                            ? 'bg-cyan text-space-950 border-cyan'
                            : 'bg-space-600 text-ink-muted'
                        }`}
                      >
                        {skill.count}
                      </Badge>
                    </Button>
                    ))
                  )}
                </div>
                {selectedSkills.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedSkills([])}
                    className="w-full mt-3 text-xs"
                  >
                    {cvService.locale === 'fr' ? 'Effacer sélection' : 'Clear selection'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Contenu principal */}
          <div className="flex-1">
            {viewMode === 'timeline' ? (
              /* Mode Timeline (chronologique) */
              <div className="relative pl-8">
                {/* Ligne verticale principale - parfaitement centrée */}
                <div className="absolute left-20 top-0 bottom-0 w-0.5 bg-gradient-to-b from-space-600 via-hair-bright to-space-700"></div>
                
                <div className="space-y-8">
                  {filteredData.map((experience, index) => {
              const isExpanded = expandedExperiences.has(experience.id);
              const title = cvService.getTranslatedValue(experience.title);
              const organization = cvService.getTranslatedValue(experience.organization);
              const location = cvService.getTranslatedValue(experience.location);
              const missionSummary = cvService.getTranslatedValue(experience.missionSummary);
              const dateRange = cvService.formatDateRange(experience.dateRange);

              return (
                <div key={experience.id} className="relative">
                  {/* Conteneur principal avec alignement parfait */}
                  <div className="flex items-start">
                    {/* Section des dates - largeur fixe et alignement parfait */}
                    <div className="w-24 flex-shrink-0 text-right pr-6 pt-2">
                      <div className="inline-block">
                        <div className="text-sm font-bold text-ink-muted bg-gradient-to-r from-space-800 to-space-900 px-3 py-1.5 rounded-lg shadow-sm border border-hair mb-1">
                          {dateRange.split(' - ')[0]}
                        </div>
                        {dateRange.includes(' - ') && (
                          <div className="text-xs font-medium text-ink-dim bg-space-800/80 px-3 py-1 rounded-md border border-hair">
                            {dateRange.split(' - ')[1]}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Point de connexion - parfaitement centré */}
                    <div className="relative flex-shrink-0 flex items-center justify-center w-6 pt-3">
                      <div className="w-4 h-4 bg-space-800 border-3 border-hair-bright rounded-full shadow-lg relative z-10 ring-4 ring-hair-bright"></div>
                      {/* Effet de brillance */}
                      <div className="absolute w-6 h-6 bg-space-700/30 rounded-full animate-pulse"></div>
                    </div>
                    
                    {/* Contenu de l'expérience */}
                    <div className="flex-1 ml-6 pt-1">
                      <Card 
                        className="bg-gradient-to-br from-space-800 to-space-900/50 backdrop-blur-sm border-hair shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:border-hair-bright rounded-xl"
                        onClick={() => toggleExperience(experience.id)}
                      >
                        <CardHeader className="pb-4 px-6 pt-5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* Ligne principale avec entreprise, titre et localisation */}
                              <div className="flex flex-wrap items-center gap-3 mb-3">
                                <CardTitle className="text-xl font-bold text-ink tracking-tight">{organization}</CardTitle>
                                <Badge variant="secondary" className="text-sm font-medium bg-gradient-to-r from-space-700 to-space-900 text-ink-muted border-hair px-3 py-1">
                                  {title}
                                </Badge>
                                {location && (
                                  <div className="flex items-center space-x-1 text-ink-dim bg-space-800/80 px-2 py-1 rounded-md border border-hair">
                                    <MapPin size={14} />
                                    <span className="text-xs font-medium">{location}</span>
                                  </div>
                                )}
                                {/* Compteur de recommandations */}
                                {experience.recommendations && experience.recommendations.length > 0 && (
                                  <div 
                                    className="flex items-center space-x-1 text-amber bg-amber/10 hover:bg-amber/15 px-2 py-1 rounded-md border border-amber/30 cursor-pointer transition-colors group"
                                    onClick={() => toggleRecommendations(experience.id)}
                                    title={cvService.locale === 'fr' ? 'Voir les recommandations' : 'View recommendations'}
                                  >
                                    <Star size={14} className="group-hover:scale-110 transition-transform" />
                                    <span className="text-xs font-medium">{experience.recommendations.length}</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Description de mission */}
                              {missionSummary && (
                                <p className="text-ink-muted text-sm leading-relaxed font-medium bg-space-800/30 p-3 rounded-lg border-l-4 border-hair-bright">
                                  {missionSummary}
                                </p>
                              )}
                            </div>
                            
                            {/* Boutons d'action */}
                            <div className="flex flex-col items-end space-y-2 ml-4">
                              {/* Contrôles locaux angle/audience - TEMPORAIREMENT COMMENTÉS */}
                              {/*
                              <div className="flex flex-col space-y-1">
                                <div className="text-xs text-ink-dim text-right">
                                  {cvService.locale === 'fr' ? 'Angle' : 'Angle'}
                                </div>
                                <AngleSelector 
                                  experienceId={experience.id}
                                  currentAngle={experienceParams[experience.id]?.angle || cvService.angle}
                                  isLoading={loadingSections.has(experience.id)}
                                />
                              </div>
                              
                              <div className="flex flex-col space-y-1">
                                <div className="text-xs text-ink-dim text-right">
                                  {cvService.locale === 'fr' ? 'Audience' : 'Audience'}
                                </div>
                                <AudienceSelector 
                                  experienceId={experience.id}
                                  currentAudience={experienceParams[experience.id]?.audience || cvService.audience}
                                  isLoading={loadingSections.has(experience.id)}
                                />
                              </div>
                              
                              {/* Indicateur de chargement */}
                              {loadingSections.has(experience.id) && (
                                <div className="flex items-center space-x-1 text-cyan bg-cyan/10 px-2 py-1 rounded-md">
                                  <Loader2 size={12} className="animate-spin" />
                                  <span className="text-xs">
                                    {cvService.locale === 'fr' ? 'Mise à jour...' : 'Updating...'}
                                  </span>
                                </div>
                              )}

                              {/* Boutons d'action existants */}
                              <div className="flex items-center space-x-2">
                                <Button
                                  size="icon"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onQuestionClick(experience.id);
                                  }}
                                  className="h-8 w-8 border-hair-bright hover:bg-space-800 hover:border-hair-bright transition-colors"
                                  title={cvService.locale === 'fr' ? 'Poser une question' : 'Ask a question'}
                                >
                                  <MessageCircle size={14} />
                                </Button>
                              
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-ink-dim hover:text-ink-muted hover:bg-space-700 transition-colors">
                                  {isExpanded ? (
                                    <ChevronUp size={18} />
                                  ) : (
                                    <ChevronDown size={18} />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardHeader>

                        {/* Contenu étendu */}
                        {isExpanded && experience.data && (
                          <CardContent className="pt-0 pb-6 px-6" onClick={(e) => e.stopPropagation()}>
                            <Separator className="mb-6 bg-gradient-to-r from-transparent via-hair to-transparent" />
                            <Accordion type="multiple" className="w-full">
                              {experience.data.map((achievement) => {
                                const achTitle = cvService.getTranslatedValue(achievement.title);
                                const achDescription = cvService.getTranslatedValue(achievement.description);
                                const extendedDescription = cvService.getTranslatedValue(achievement.extendedDescription);

                                return (
                                  <AccordionItem key={achievement.id} value={achievement.id} className="border-hair rounded-lg mb-2 bg-space-800/30">
                                    <AccordionTrigger 
                                      className="hover:no-underline text-ink-muted px-4 py-3 hover:bg-space-800/50 rounded-lg transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between w-full pr-4">
                                        <div className="flex items-center space-x-3">
                                          <h4 className="text-base font-semibold text-left text-ink">{achTitle}</h4>
                                          {achievement.url && (
                                            <Button 
                                              variant="ghost" 
                                              size="icon" 
                                              asChild
                                              onClick={(e) => e.stopPropagation()}
                                              className="h-7 w-7 hover:bg-space-700 border border-hair"
                                            >
                                              <a
                                                href={achievement.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                <ExternalLink size={14} />
                                              </a>
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </AccordionTrigger>
                                    
                                    <AccordionContent className="px-4 pb-4">
                                      <div className="space-y-4 pt-2">
                                        {achDescription && (
                                          <div className="bg-space-800/70 p-4 rounded-lg border border-hair shadow-sm">
                                            <p className="text-ink-muted font-medium text-base leading-relaxed">{achDescription}</p>
                                          </div>
                                        )}
                                        
                                        {extendedDescription && (
                                          <div className="bg-space-800/50 p-4 rounded-lg border-l-4 border-hair-bright">
                                            <p className="text-ink-muted leading-relaxed text-base">{extendedDescription}</p>
                                          </div>
                                        )}
                                        
                                        {/* Skills Used */}
                                        {achievement.skillsUsed && achievement.skillsUsed.length > 0 && (
                                          <div className="bg-space-800/60 p-4 rounded-lg border border-hair">
                                            <h5 className="text-sm font-semibold mb-3 text-ink-muted flex items-center">
                                              <div className="w-2 h-2 bg-space-600 rounded-full mr-2"></div>
                                              {cvService.locale === 'fr' ? 'Compétences utilisées' : 'Skills used'}
                                            </h5>
                                            <div className="flex flex-wrap gap-2">
                                              {achievement.skillsUsed.map((skill, index) => {
                                                const skillName = cvService.getTranslatedValue(skill.id);
                                                return (
                                                  <Badge 
                                                    key={index} 
                                                    variant="secondary" 
                                                    className="text-xs bg-gradient-to-r from-space-700 to-space-900 text-ink-muted border-hair px-3 py-1 font-medium cursor-pointer hover:bg-space-600 transition-colors"
                                                    onClick={() => handleSkillClick(skillName)}
                                                  >
                                                    <span>{skillName}</span>
                                                    {skill.level && (
                                                      <span className="ml-2">
                                                        {renderSkillLevel(skill.level)}
                                                      </span>
                                                    )}
                                                  </Badge>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Carrousel d'images */}
                                        <TimelineImageCarousel 
                                          images={achievement.images_url} 
                                          achievementId={achievement.id} 
                                        />
                                        
                                        {/* Q&A Preview */}
                                        {achievement.qa && achievement.qa.length > 0 && (
                                          <div className="bg-gradient-to-br from-space-800 to-space-900 p-4 rounded-lg border border-hair shadow-sm">
                                            <p className="text-sm font-semibold mb-3 text-ink-muted flex items-center">
                                              <MessageCircle size={14} className="mr-2" />
                                              {cvService.locale === 'fr' ? 'Questions disponibles' : 'Available questions'}
                                            </p>
                                            <div className="space-y-2">
                                              {achievement.qa.slice(0, 2).map((qa, index) => (
                                                <Button
                                                  key={index}
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => onQuestionClick(experience.id, cvService.getTranslatedValue(qa.question))}
                                                  className="h-auto p-3 justify-start text-left text-sm text-ink-muted hover:text-ink hover:bg-space-700 border border-hair hover:border-hair transition-all w-full rounded-lg"
                                                >
                                                  <span className="text-ink-dim mr-2">•</span>
                                                  {cvService.getTranslatedValue(qa.question)}
                                                </Button>
                                              ))}
                                              {achievement.qa.length > 2 && (
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => onQuestionClick(experience.id)}
                                                  className="text-sm text-ink-dim hover:text-ink-muted hover:bg-space-700 px-3 py-2 rounded-lg border border-hair"
                                                >
                                                  {cvService.locale === 'fr' ? 
                                                    `+${achievement.qa.length - 2} autres questions` : 
                                                    `+${achievement.qa.length - 2} more questions`
                                                  }
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                );
                              })}
                            </Accordion>
                            
                            {/* Recommandations de l'entreprise */}
                            {experience.recommendations && experience.recommendations.length > 0 && (
                              <div className="px-6 pb-4">
                                <RecommendationsSection 
                                  recommendations={experience.recommendations} 
                                  experienceId={experience.id} 
                                />
                              </div>
                            )}
                          </CardContent>
                        )}
                        
                      </Card>
                    </div>
                  </div>
                </div>
              );
                  })}
                </div>
              </div>
            ) : (
              /* Mode Skills (compétences) */
              <div className="space-y-4">
                {selectedSkills.length === 0 ? (
                  <div className="text-center py-12 text-ink-dim">
                    <Wrench size={48} className="mx-auto mb-4 text-ink-dim" />
                    <p className="text-lg font-medium mb-2">
                      {cvService.locale === 'fr' ? 'Sélectionnez des compétences' : 'Select skills'}
                    </p>
                    <p className="text-sm">
                      {cvService.locale === 'fr' 
                        ? 'Choisissez une ou plusieurs compétences dans la sidebar pour filtrer les expériences.' 
                        : 'Choose one or more skills from the sidebar to filter experiences.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Header avec résumé des filtres */}
                    <div className="bg-cyan/10 border border-cyan/40 rounded-lg p-4 mb-6">
                      <h3 className="text-sm font-semibold text-cyan-soft mb-2">
                        {cvService.locale === 'fr' ? 'Filtré par compétences' : 'Filtered by skills'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedSkills.map(skill => (
                          <Badge 
                            key={skill} 
                            variant="default" 
                            className="bg-cyan text-space-950 cursor-pointer hover:bg-cyan-soft transition-colors flex items-center"
                            onClick={() => toggleSkill(skill)}
                          >
                            <span>{skill}</span>
                            <button className="ml-2 hover:bg-cyan-soft rounded-full w-4 h-4 flex items-center justify-center text-xs">
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-cyan mt-2">
                        {filteredData.length} {cvService.locale === 'fr' ? 'expérience(s) trouvée(s)' : 'experience(s) found'}
                      </p>
                    </div>

                    {/* Liste des expériences filtrées */}
                    {filteredData.map(experience => 
                      experience.data?.map(achievement => (
                        <SkillExperienceCard
                          key={`${experience.id}-${achievement.id}`}
                          achievement={achievement}
                          experience={experience}
                          selectedSkills={selectedSkills}
                          cvService={cvService}
                          onSkillClick={handleSkillClick}
                          showImages={showImages}
                        />
                      ))
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineSection;
